/**
 * Funciones auxiliares generales
 * Monedero Digital NFC
 */

import CryptoJS from 'crypto-js';
import { APP_CONFIG } from './constants';

/**
 * Genera un ID único
 */
export const generateId = (prefix = 'id') => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  return `${prefix}_${timestamp}_${random}`;
};

/**
 * Genera un ID para transacción
 */
export const generateTransactionId = () => {
  const date = new Date();
  const dateStr = date.toISOString().split('T')[0].replace(/-/g, '');
  const timeStr = date.getTime().toString().slice(-6);
  const random = Math.random().toString(36).substr(2, 4);
  return `txn_${dateStr}_${timeStr}_${random}`;
};

/**
 * Genera un ID para producto
 */
export const generateProductId = () => {
  return generateId('prod');
};

/**
 * Calcula el total de un carrito de productos
 */
export const calculateCartTotal = (cart) => {
  if (!cart || cart.length === 0) return 0;
  
  return cart.reduce((total, item) => {
    return total + (item.precio * (item.cantidad || 1));
  }, 0);
};

/**
 * Calcula el nuevo saldo después de una operación
 */
export const calculateNewBalance = (currentBalance, amount, isRecharge = false) => {
  const numBalance = Number(currentBalance) || 0;
  const numAmount = Number(amount) || 0;
  
  return isRecharge ? numBalance + numAmount : numBalance - numAmount;
};

/**
 * Genera un hash de verificación para integridad de datos
 */
export const generateDataHash = (data) => {
  try {
    const dataString = typeof data === 'string' ? data : JSON.stringify(data);
    return CryptoJS.SHA256(dataString).toString();
  } catch (error) {
    console.error('Error generando hash:', error);
    return null;
  }
};

/**
 * Verifica un hash de datos
 */
export const verifyDataHash = (data, hash) => {
  const generatedHash = generateDataHash(data);
  return generatedHash === hash;
};

/**
 * Clona profundamente un objeto
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (error) {
    console.error('Error clonando objeto:', error);
    return obj;
  }
};

/**
 * Ordena un array de objetos por una propiedad
 */
export const sortByProperty = (array, property, descending = false) => {
  const sorted = [...array].sort((a, b) => {
    const aVal = a[property];
    const bVal = b[property];
    
    if (aVal < bVal) return descending ? 1 : -1;
    if (aVal > bVal) return descending ? -1 : 1;
    return 0;
  });
  
  return sorted;
};

/**
 * Filtra un array por múltiples criterios
 */
export const filterByMultipleCriteria = (array, criteria) => {
  return array.filter(item => {
    return Object.keys(criteria).every(key => {
      const criteriaValue = criteria[key];
      const itemValue = item[key];
      
      if (criteriaValue === null || criteriaValue === undefined || criteriaValue === '') {
        return true; // Ignora criterios vacíos
      }
      
      if (typeof criteriaValue === 'string') {
        return itemValue?.toString().toLowerCase().includes(criteriaValue.toLowerCase());
      }
      
      return itemValue === criteriaValue;
    });
  });
};

/**
 * Agrupa un array por una propiedad
 */
export const groupBy = (array, property) => {
  return array.reduce((groups, item) => {
    const key = item[property];
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {});
};

/**
 * Obtiene elementos únicos de un array
 */
export const getUnique = (array, property = null) => {
  if (!property) {
    return [...new Set(array)];
  }
  
  const seen = new Set();
  return array.filter(item => {
    const value = item[property];
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
};

/**
 * Valida si estamos en modo desarrollo
 */
export const isDevelopment = () => {
  return APP_CONFIG.ENV === 'development';
};

/**
 * Valida si el simulador NFC está habilitado
 */
export const isSimulatorEnabled = () => {
  return APP_CONFIG.ENABLE_SIMULATOR && isDevelopment();
};

/**
 * Espera un tiempo determinado (útil para simular delays)
 */
export const sleep = (ms) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Ejecuta una función con retry en caso de error
 */
export const retry = async (fn, maxAttempts = 3, delay = 1000) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      console.warn(`Intento ${attempt} fallido:`, error.message);
      
      if (attempt < maxAttempts) {
        await sleep(delay);
      }
    }
  }
  
  throw lastError;
};

/**
 * Sanitiza un string para prevenir XSS
 */
export const sanitizeString = (str) => {
  if (!str) return '';
  
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };
  
  return str.replace(/[&<>"'/]/g, char => map[char]);
};

/**
 * Trunca decimales sin redondear
 */
export const truncateDecimals = (number, decimals = 0) => {
  const factor = Math.pow(10, decimals);
  return Math.floor(number * factor) / factor;
};

/**
 * Verifica si un objeto está vacío
 */
export const isEmpty = (obj) => {
  if (obj === null || obj === undefined) return true;
  if (Array.isArray(obj)) return obj.length === 0;
  if (typeof obj === 'object') return Object.keys(obj).length === 0;
  return false;
};

/**
 * Obtiene un valor anidado de un objeto de forma segura
 */
export const getNestedValue = (obj, path, defaultValue = null) => {
  try {
    const value = path.split('.').reduce((current, key) => current?.[key], obj);
    return value !== undefined ? value : defaultValue;
  } catch (error) {
    return defaultValue;
  }
};

/**
 * Debounce function
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function
 */
export const throttle = (func, limit = 300) => {
  let inThrottle;
  
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Compara dos versiones (ej: "1.0.0" vs "1.0.1")
 */
export const compareVersions = (v1, v2) => {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);
  
  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] || 0;
    const part2 = parts2[i] || 0;
    
    if (part1 > part2) return 1;
    if (part1 < part2) return -1;
  }
  
  return 0;
};

export default {
  generateId,
  generateTransactionId,
  generateProductId,
  calculateCartTotal,
  calculateNewBalance,
  generateDataHash,
  verifyDataHash,
  deepClone,
  sortByProperty,
  filterByMultipleCriteria,
  groupBy,
  getUnique,
  isDevelopment,
  isSimulatorEnabled,
  sleep,
  retry,
  sanitizeString,
  truncateDecimals,
  isEmpty,
  getNestedValue,
  debounce,
  throttle,
  compareVersions,
};
